import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
  LineChart, Line, CartesianGrid, Legend,
} from 'recharts';
import {
  Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, PhoneOff,
  Upload, Download, Search, Users, TrendingUp, Sparkles, Loader2, X, Filter, RefreshCw,
  Pencil, Check,
} from 'lucide-react';

import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { API } from '../utils/apiConfig';

const DATE_PRESETS = [
  ['today', 'Today'], ['yesterday', 'Yesterday'],
  ['last_7', 'Last 7 days'], ['last_30', 'Last 30 days'],
  ['this_week', 'This Week'], ['last_week', 'Last Week'],
  ['this_month', 'This Month'], ['last_month', 'Last Month'],
  ['this_quarter', 'This Quarter'], ['last_quarter', 'Last Quarter'],
  ['this_year', 'This Year'], ['last_year', 'Last Year'],
  ['custom', 'Custom Range'], ['all', 'All Time'],
];

const STATUS_COLORS = { answered: '#10b981', missed: '#ef4444', busy: '#f59e0b', failed: '#8b5cf6', cancelled: '#94a3b8' };
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const fmtSec = (s) => {
  const n = Number(s || 0);
  if (n < 60) return `${Math.round(n)}s`;
  const m = Math.floor(n / 60); const r = Math.round(n % 60);
  return `${m}m ${r}s`;
};

const errMsg = (e) => e?.response?.data?.detail || e?.message || 'Something went wrong';

const KPI = ({ label, value, icon: Icon, tone = 'text-slate-800' }) => (
  <Card className="p-3">
    <div className="flex items-center gap-2 text-xs text-slate-500">
      {Icon ? <Icon className="w-3.5 h-3.5" /> : null} {label}
    </div>
    <div className={`text-2xl font-bold ${tone}`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>{value}</div>
  </Card>
);

export default function CallingDataPage() {
  const [filters, setFilters] = useState({
    preset: 'this_month', cf: '', ct: '',
    staff: '', call_type: '', call_status: '', team: '', sf: '', tags: '',
    customer: '', phone: '', search: '',
  });
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [facets, setFacets] = useState({});
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState({ total: 0, rows: [], page: 1, page_size: 50 });
  const [sortConfig, setSortConfig] = useState({ key: 'start_time', dir: 'desc' });

  const [showImport, setShowImport] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const [customerProfile, setCustomerProfile] = useState(null);
  const [nameEdit, setNameEdit] = useState(null); // {phone, name} while editing
  const [nameSaving, setNameSaving] = useState(false);

  const params = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [filters]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [dash, ins, fac, recs] = await Promise.all([
        axios.get(`${API}/calling-data/dashboard`, { params }),
        axios.get(`${API}/calling-data/insights`, { params: { preset: filters.preset, cf: filters.cf, ct: filters.ct } }),
        axios.get(`${API}/calling-data/facets`),
        axios.get(`${API}/calling-data/records`, { params: { ...params, page: 1, page_size: 50, sort_by: sortConfig.key, sort_dir: sortConfig.dir } }),
      ]);
      setData(dash.data); setInsights(ins.data); setFacets(fac.data); setRecords(recs.data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [params, filters.preset, filters.cf, filters.ct, sortConfig.key, sortConfig.dir]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const resetFilters = () => setFilters({
    preset: 'this_month', cf: '', ct: '',
    staff: '', call_type: '', call_status: '', team: '', sf: '', tags: '',
    customer: '', phone: '', search: '',
  });

  const doImport = async (file) => {
    if (!file) return;
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/calling-data/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(r.data);
      toast.success(`Imported ${r.data.imported}, updated ${r.data.updated}`);
      fetchAll();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setImporting(false); }
  };

  const openCustomer = async (phone) => {
    if (!phone) return;
    try {
      const r = await axios.get(`${API}/calling-data/customers/${encodeURIComponent(phone)}`);
      setCustomerProfile(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const exportCsv = () => {
    const qs = new URLSearchParams(params).toString();
    window.open(`${API}/calling-data/export?${qs}`, '_blank');
  };

  const openEditName = (phone, currentName, ev) => {
    if (ev) { ev.stopPropagation(); ev.preventDefault(); }
    if (!phone) return;
    setNameEdit({ phone, name: currentName || '' });
  };

  const saveEditName = async () => {
    if (!nameEdit) return;
    const name = (nameEdit.name || '').trim();
    if (!name) { toast.error('Name cannot be empty'); return; }
    setNameSaving(true);
    try {
      const r = await axios.put(
        `${API}/calling-data/customers/${encodeURIComponent(nameEdit.phone)}`,
        { name },
      );
      toast.success(`Updated ${r.data.calls_updated} record(s)`);
      // Reflect the change locally without refetching the full dashboard.
      setRecords((prev) => ({
        ...prev,
        rows: prev.rows.map((row) =>
          row.customer_phone === r.data.phone ? { ...row, customer: name } : row,
        ),
      }));
      setData((prev) => prev ? ({
        ...prev,
        top_customers: (prev.top_customers || []).map((c) =>
          c.phone === r.data.phone ? { ...c, customer: name } : c,
        ),
      }) : prev);
      setCustomerProfile((prev) =>
        prev && prev.phone === r.data.phone ? { ...prev, customer: name } : prev,
      );
      setNameEdit(null);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setNameSaving(false); }
  };

  const c = data?.counts || {};
  const cm = data?.customer_metrics || {};
  const perf = data?.performance || {};
  const staffSorted = data?.staff_leaderboard || [];
  const mostActive = staffSorted[0];
  const leastActive = staffSorted[staffSorted.length - 1];

  const toggleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    });
  };
  const sortIcon = (key) => {
    if (sortConfig.key !== key) return <span className="ml-1 text-slate-300">↕</span>;
    return <span className="ml-1 text-emerald-600">{sortConfig.dir === 'asc' ? '▲' : '▼'}</span>;
  };

  return (
    <DashboardLayout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Phone className="w-6 h-6 text-emerald-600" /> Calling Data
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">Smartflo call analytics — {data?.total || 0} calls in view.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchAll} disabled={loading} data-testid="calldata-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button variant="outline" onClick={exportCsv} data-testid="calldata-export"><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setShowImport(true)} data-testid="calldata-import-btn"><Upload className="w-4 h-4 mr-1" /> Import</Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-3 mb-4">
        <div className="flex flex-wrap gap-2 items-end">
          <div className="min-w-[160px] flex-1">
            <Label className="text-xs">Date Range</Label>
            <Select value={filters.preset} onValueChange={(v) => setF('preset', v)}>
              <SelectTrigger data-testid="filter-preset"><SelectValue /></SelectTrigger>
              <SelectContent>{DATE_PRESETS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {filters.preset === 'custom' && (
            <>
              <div><Label className="text-xs">From</Label><Input type="date" value={filters.cf} onChange={(e) => setF('cf', e.target.value)} data-testid="filter-cf" /></div>
              <div><Label className="text-xs">To</Label><Input type="date" value={filters.ct} onChange={(e) => setF('ct', e.target.value)} data-testid="filter-ct" /></div>
            </>
          )}
          <div className="min-w-[140px]">
            <Label className="text-xs">Staff</Label>
            <Select value={filters.staff || 'all'} onValueChange={(v) => setF('staff', v === 'all' ? '' : v)}>
              <SelectTrigger data-testid="filter-staff"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {(facets.staff || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[130px]">
            <Label className="text-xs">Type</Label>
            <Select value={filters.call_type || 'all'} onValueChange={(v) => setF('call_type', v === 'all' ? '' : v)}>
              <SelectTrigger data-testid="filter-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem>{(facets.call_types || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="min-w-[130px]">
            <Label className="text-xs">Status</Label>
            <Select value={filters.call_status || 'all'} onValueChange={(v) => setF('call_status', v === 'all' ? '' : v)}>
              <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem>{(facets.call_statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="min-w-[200px] flex-1">
            <Label className="text-xs">Search</Label>
            <Input placeholder="Customer / phone / call id / notes" value={filters.search} onChange={(e) => setF('search', e.target.value)} data-testid="filter-search" />
          </div>
          <Button variant="outline" onClick={resetFilters}><Filter className="w-4 h-4 mr-1" /> Reset</Button>
        </div>
      </Card>

      {/* KPI cards */}
      {loading || !data ? <div className="text-center py-8 text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div> : (
      <>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-3">
        <KPI label="Total Calls" value={c.total || 0} icon={Phone} />
        <KPI label="Incoming" value={c.incoming || 0} icon={PhoneIncoming} tone="text-blue-600" />
        <KPI label="Outgoing" value={c.outgoing || 0} icon={PhoneOutgoing} tone="text-indigo-600" />
        <KPI label="Answered" value={c.answered || 0} tone="text-emerald-600" />
        <KPI label="Missed" value={c.missed || 0} icon={PhoneMissed} tone="text-red-600" />
        <KPI label="Busy" value={c.busy || 0} tone="text-amber-600" />
        <KPI label="Failed" value={c.failed || 0} icon={PhoneOff} tone="text-purple-600" />
        <KPI label="Cancelled" value={c.cancelled || 0} tone="text-slate-500" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 mb-3">
        <KPI label="Unique Customers" value={cm.unique_customers || 0} icon={Users} />
        <KPI label="New" value={cm.new_customers || 0} tone="text-emerald-700" />
        <KPI label="Repeat" value={cm.repeat_customers || 0} tone="text-indigo-700" />
        <KPI label="Contacted Today" value={cm.contacted_today || 0} />
        <KPI label="Answer Rate" value={`${perf.answer_rate}%`} tone="text-emerald-700" />
        <KPI label="Miss Rate" value={`${perf.miss_rate}%`} tone="text-red-600" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 mb-4">
        <KPI label="Avg Duration" value={fmtSec(perf.avg_duration_sec)} />
        <KPI label="Avg Ring" value={fmtSec(perf.avg_ring_sec)} />
        <KPI label="Avg/Customer" value={perf.avg_calls_per_customer} />
        <KPI label="Avg/Staff" value={perf.avg_calls_per_staff} />
        <KPI label="First Call Res" value={`${perf.first_call_resolution_pct}%`} />
        <KPI label="Repeat Rate" value={`${perf.repeat_contact_rate}%`} />
      </div>

      {/* AI insights */}
      {insights && (
        <Card className="p-4 mb-4 bg-gradient-to-r from-indigo-50 to-emerald-50 border-indigo-200">
          <h3 className="font-bold text-slate-800 mb-2 flex items-center gap-2"><Sparkles className="w-4 h-4 text-indigo-600" /> AI Insights</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div><b>Best Hour:</b> {insights.best_hour || '—'}</div>
            <div><b>Best Day:</b> {insights.best_day || '—'}</div>
            <div><b>Best Staff:</b> {insights.best_performing_staff || '—'}</div>
            <div><b>Highest Answer %:</b> {insights.highest_answer_rate?.staff || '—'} ({insights.highest_answer_rate?.rate_pct ?? 0}%)</div>
            <div><b>Lowest Answer %:</b> {insights.lowest_answer_rate?.staff || '—'} ({insights.lowest_answer_rate?.rate_pct ?? 0}%)</div>
            <div><b>Longest Avg Talk:</b> {insights.longest_talk_time_staff?.staff || '—'} ({fmtSec(insights.longest_talk_time_staff?.avg_duration_sec)})</div>
            <div><b>Top Tag:</b> {insights.top_tag || '—'}</div>
            <div><b>Top Status:</b> {insights.top_status || '—'}</div>
          </div>
        </Card>
      )}

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        <Card className="p-4" data-testid="chart-trend">
          <h3 className="font-semibold mb-2 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-600" /> Daily Trend</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.trend_daily}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Line dataKey="count" stroke="#10b981" dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-4" data-testid="chart-status">
          <h3 className="font-semibold mb-2">Call Status</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data.status_chart} dataKey="count" nameKey="status" label>
                {data.status_chart.map((e, i) => <Cell key={i} fill={STATUS_COLORS[e.status] || '#64748b'} />)}
              </Pie>
              <Tooltip /><Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-4" data-testid="chart-type">
          <h3 className="font-semibold mb-2">Incoming vs Outgoing</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data.type_chart} dataKey="count" nameKey="type" label>
                <Cell fill="#3b82f6" /><Cell fill="#8b5cf6" />
              </Pie>
              <Tooltip /><Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Charts row 2 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <Card className="p-4" data-testid="chart-staff">
          <h3 className="font-semibold mb-2">Calls by Staff</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={staffSorted.slice(0, 15)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="staff" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={70} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="calls" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-4" data-testid="chart-tags">
          <h3 className="font-semibold mb-2">Calls by Tags (Top 15)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={(data.tags_chart || []).slice(0, 15)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="tag" tick={{ fontSize: 10 }} width={140} />
              <Tooltip />
              <Bar dataKey="count" fill="#8b5cf6" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Charts row 3 — heatmap + weekday + repeat dist + top customers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <Card className="p-4" data-testid="chart-heatmap">
          <h3 className="font-semibold mb-2">Peak Calling Hours (Hour × Day)</h3>
          <Heatmap data={data.heatmap} />
        </Card>
        <Card className="p-4" data-testid="chart-weekday">
          <h3 className="font-semibold mb-2">Day of Week Analysis</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.weekday.map((r) => ({ day: DAYS[r.day], count: r.count }))}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <Card className="p-4" data-testid="table-top-customers">
          <h3 className="font-semibold mb-2">Top 20 Customers</h3>
          <div className="max-h-[280px] overflow-y-auto text-sm">
            <table className="w-full">
              <thead className="text-xs text-slate-500"><tr>
                <th className="text-left px-2 py-1">Customer</th>
                <th className="text-left px-2 py-1">Phone</th>
                <th className="text-right px-2 py-1">Calls</th>
                <th className="text-left px-2 py-1">Last</th>
              </tr></thead>
              <tbody>{(data.top_customers || []).map((c) => (
                <tr key={c.phone} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => openCustomer(c.phone)}>
                  <td className="px-2 py-1 text-blue-700 underline decoration-dotted">
                    <span className="inline-flex items-center gap-1">
                      {c.customer || '(no name)'}
                      <button
                        type="button"
                        className="text-slate-400 hover:text-emerald-600"
                        title="Edit name"
                        onClick={(e) => openEditName(c.phone, c.customer, e)}
                        data-testid={`edit-name-top-${c.phone}`}
                      ><Pencil className="w-3 h-3" /></button>
                    </span>
                  </td>
                  <td className="px-2 py-1 font-mono text-xs">{c.phone}</td>
                  <td className="px-2 py-1 text-right font-bold">{c.calls}</td>
                  <td className="px-2 py-1 text-xs text-slate-500">{c.last_call?.slice(0, 16).replace('T', ' ')}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </Card>
        <Card className="p-4" data-testid="chart-repeat-dist">
          <h3 className="font-semibold mb-2">Repeat Customer Distribution</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.repeat_distribution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="bucket" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#f59e0b" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Staff leaderboard */}
      <Card className="p-4 mb-4" data-testid="staff-leaderboard">
        <h3 className="font-semibold mb-2">Staff Leaderboard</h3>
        <div className="overflow-x-auto text-sm">
          <table className="w-full">
            <thead className="text-xs text-slate-500 bg-slate-50"><tr>
              <th className="text-left px-3 py-1.5">Staff</th>
              <th className="text-right px-3 py-1.5">Calls</th>
              <th className="text-right px-3 py-1.5">Answered</th>
              <th className="text-right px-3 py-1.5">Missed</th>
              <th className="text-right px-3 py-1.5">Answer %</th>
              <th className="text-right px-3 py-1.5">Customers</th>
              <th className="text-right px-3 py-1.5">Avg Duration</th>
            </tr></thead>
            <tbody>{staffSorted.map((s) => {
              const pct = s.calls ? Math.round(1000 * s.answered / s.calls) / 10 : 0;
              return (
                <tr key={s.staff} className="border-t">
                  <td className="px-3 py-1.5 font-medium">{s.staff}</td>
                  <td className="px-3 py-1.5 text-right">{s.calls}</td>
                  <td className="px-3 py-1.5 text-right text-emerald-700">{s.answered}</td>
                  <td className="px-3 py-1.5 text-right text-red-600">{s.missed}</td>
                  <td className="px-3 py-1.5 text-right font-bold">{pct}%</td>
                  <td className="px-3 py-1.5 text-right">{s.customers_contacted}</td>
                  <td className="px-3 py-1.5 text-right">{fmtSec(s.avg_duration)}</td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
        {mostActive && leastActive && (
          <div className="text-xs text-slate-500 mt-2">
            Most Active: <b>{mostActive.staff}</b> ({mostActive.calls}) · Least Active: <b>{leastActive.staff}</b> ({leastActive.calls})
          </div>
        )}
      </Card>

      {/* Records */}
      <Card className="p-4" data-testid="records-table">
        <h3 className="font-semibold mb-2">Call Records ({records.total.toLocaleString('en-IN')} total)</h3>
        <div className="overflow-x-auto text-sm max-h-[500px] overflow-y-auto">
          <table className="w-full">
            <thead className="text-xs text-slate-500 bg-slate-50 sticky top-0"><tr>
              <th className="text-left px-3 py-1.5 cursor-pointer select-none hover:text-slate-900" onClick={() => toggleSort('start_time')} data-testid="sort-start-time">Start Time{sortIcon('start_time')}</th>
              <th className="text-left px-3 py-1.5 cursor-pointer select-none hover:text-slate-900" onClick={() => toggleSort('customer')} data-testid="sort-customer">Customer{sortIcon('customer')}</th>
              <th className="text-left px-3 py-1.5 cursor-pointer select-none hover:text-slate-900" onClick={() => toggleSort('customer_phone')} data-testid="sort-phone">Phone{sortIcon('customer_phone')}</th>
              <th className="text-left px-3 py-1.5 cursor-pointer select-none hover:text-slate-900" onClick={() => toggleSort('call_type')} data-testid="sort-type">Type{sortIcon('call_type')}</th>
              <th className="text-left px-3 py-1.5 cursor-pointer select-none hover:text-slate-900" onClick={() => toggleSort('call_status')} data-testid="sort-status">Status{sortIcon('call_status')}</th>
              <th className="text-right px-3 py-1.5 cursor-pointer select-none hover:text-slate-900" onClick={() => toggleSort('duration_sec')} data-testid="sort-duration">Duration{sortIcon('duration_sec')}</th>
              <th className="text-left px-3 py-1.5 cursor-pointer select-none hover:text-slate-900" onClick={() => toggleSort('staff')} data-testid="sort-staff">Staff{sortIcon('staff')}</th>
              <th className="text-left px-3 py-1.5">Tags</th>
            </tr></thead>
            <tbody>{records.rows.map((r) => (
              <tr key={r.call_id} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => openCustomer(r.customer_phone)}>
                <td className="px-3 py-1.5 text-xs">{r.start_time?.slice(0, 19).replace('T', ' ')}</td>
                <td className="px-3 py-1.5 text-blue-700">
                  <span className="inline-flex items-center gap-1">
                    {r.customer || <span className="text-slate-400">(no name)</span>}
                    <button
                      type="button"
                      className="text-slate-400 hover:text-emerald-600"
                      title="Edit name"
                      onClick={(e) => openEditName(r.customer_phone, r.customer, e)}
                      data-testid={`edit-name-row-${r.call_id}`}
                    ><Pencil className="w-3 h-3" /></button>
                  </span>
                </td>
                <td className="px-3 py-1.5 font-mono text-xs">{r.customer_phone}</td>
                <td className="px-3 py-1.5">{r.call_type}</td>
                <td className="px-3 py-1.5">
                  <span className="px-1.5 py-0.5 rounded text-xs" style={{ background: (STATUS_COLORS[r.call_status] || '#94a3b8') + '22', color: STATUS_COLORS[r.call_status] || '#334155' }}>{r.call_status}</span>
                </td>
                <td className="px-3 py-1.5 text-right">{fmtSec(r.duration_sec)}</td>
                <td className="px-3 py-1.5">{r.staff}</td>
                <td className="px-3 py-1.5 text-xs text-slate-500">{(r.tags || []).join(', ')}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <Paginator records={records} setRecords={setRecords} params={params} sortConfig={sortConfig} />
      </Card>
      </>)}

      {/* Import dialog */}
      <Dialog open={showImport} onOpenChange={(o) => { setShowImport(o); if (!o) setImportResult(null); }}>
        <DialogContent className="sm:max-w-[520px]" data-testid="import-dialog">
          <DialogHeader><DialogTitle>Import Smartflo Call History</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Upload the Excel or CSV export from Smartflo. Existing Call IDs will be updated, duplicates skipped. Recording column is ignored.
            </p>
            <input ref={fileRef} type="file" accept=".csv,.xls,.xlsx" onChange={(e) => doImport(e.target.files?.[0])} className="text-sm" data-testid="import-file" />
            {importing && <div className="text-center py-3"><Loader2 className="w-5 h-5 animate-spin inline" /> Importing…</div>}
            {importResult && (
              <div className="grid grid-cols-5 gap-2 text-center">
                <Card className="p-2"><div className="text-xs text-slate-500">Total</div><div className="text-xl font-bold" data-testid="imp-total">{importResult.total}</div></Card>
                <Card className="p-2 bg-emerald-50"><div className="text-xs text-emerald-700">Imported</div><div className="text-xl font-bold text-emerald-700" data-testid="imp-imported">{importResult.imported}</div></Card>
                <Card className="p-2 bg-blue-50"><div className="text-xs text-blue-700">Updated</div><div className="text-xl font-bold text-blue-700" data-testid="imp-updated">{importResult.updated}</div></Card>
                <Card className="p-2 bg-amber-50"><div className="text-xs text-amber-700">Skipped</div><div className="text-xl font-bold text-amber-700" data-testid="imp-skipped">{importResult.skipped}</div></Card>
                <Card className="p-2 bg-red-50"><div className="text-xs text-red-700">Invalid</div><div className="text-xl font-bold text-red-700" data-testid="imp-invalid">{importResult.invalid}</div></Card>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImport(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer profile dialog */}
      <Dialog open={!!customerProfile} onOpenChange={(o) => !o && setCustomerProfile(null)}>
        <DialogContent className="sm:max-w-[720px] max-h-[92vh] overflow-y-auto" data-testid="customer-profile-dialog">
          <DialogHeader><DialogTitle>
            <span className="inline-flex items-center gap-2">
              {customerProfile?.customer || '(No Name)'} · {customerProfile?.phone}
              <button
                type="button"
                className="text-slate-400 hover:text-emerald-600"
                title="Edit name"
                onClick={() => openEditName(customerProfile?.phone, customerProfile?.customer)}
                data-testid="edit-name-profile"
              ><Pencil className="w-4 h-4" /></button>
            </span>
          </DialogTitle></DialogHeader>
          {customerProfile && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-4 gap-2 text-center">
                <Card className="p-2"><div className="text-xs">Total</div><div className="text-xl font-bold">{customerProfile.total_calls}</div></Card>
                <Card className="p-2"><div className="text-xs">Incoming</div><div className="text-xl font-bold text-blue-700">{customerProfile.incoming}</div></Card>
                <Card className="p-2"><div className="text-xs">Outgoing</div><div className="text-xl font-bold text-indigo-700">{customerProfile.outgoing}</div></Card>
                <Card className="p-2"><div className="text-xs">Answer %</div><div className="text-xl font-bold text-emerald-700">{customerProfile.answer_pct}%</div></Card>
              </div>
              <div className="text-xs text-slate-500">
                Assigned Staff: <b>{customerProfile.assigned_staff || '—'}</b> · Latest: {customerProfile.latest_call?.slice(0, 16).replace('T', ' ')} · Tags: {customerProfile.tags?.join(', ') || '—'}
              </div>
              <h4 className="font-semibold mt-3">Timeline</h4>
              <div className="max-h-[380px] overflow-y-auto border rounded-md">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0"><tr>
                    <th className="text-left px-2 py-1">Time</th>
                    <th className="text-left px-2 py-1">Type</th>
                    <th className="text-left px-2 py-1">Status</th>
                    <th className="text-right px-2 py-1">Dur</th>
                    <th className="text-left px-2 py-1">Staff</th>
                    <th className="text-left px-2 py-1">Notes</th>
                  </tr></thead>
                  <tbody>{customerProfile.timeline.map((r) => (
                    <tr key={r.call_id} className="border-t">
                      <td className="px-2 py-1">{r.start_time?.slice(0, 16).replace('T', ' ')}</td>
                      <td className="px-2 py-1">{r.call_type}</td>
                      <td className="px-2 py-1">{r.call_status}</td>
                      <td className="px-2 py-1 text-right">{fmtSec(r.duration_sec)}</td>
                      <td className="px-2 py-1">{r.staff}</td>
                      <td className="px-2 py-1 text-slate-500 truncate max-w-[200px]">{r.notes}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomerProfile(null)}><X className="w-4 h-4 mr-1" /> Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit customer name dialog */}
      <Dialog open={!!nameEdit} onOpenChange={(o) => !o && !nameSaving && setNameEdit(null)}>
        <DialogContent className="sm:max-w-[420px]" data-testid="edit-name-dialog">
          <DialogHeader><DialogTitle>Edit Customer Name</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            <div className="text-xs text-slate-500">
              This will rename every call record for phone{' '}
              <span className="font-mono">{nameEdit?.phone}</span>.
            </div>
            <Label htmlFor="edit-name-input">Customer Name</Label>
            <Input
              id="edit-name-input"
              autoFocus
              value={nameEdit?.name || ''}
              onChange={(e) => setNameEdit((s) => ({ ...s, name: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') saveEditName(); }}
              placeholder="Enter customer name"
              data-testid="edit-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNameEdit(null)} disabled={nameSaving}>Cancel</Button>
            <Button onClick={saveEditName} disabled={nameSaving} data-testid="edit-name-save">
              {nameSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function Paginator({ records, setRecords, params, sortConfig }) {
  const totalPages = Math.max(1, Math.ceil((records.total || 0) / (records.page_size || 50)));
  const go = async (page) => {
    if (page < 1 || page > totalPages) return;
    try {
      const r = await axios.get(`${API}/calling-data/records`, {
        params: {
          ...params, page, page_size: records.page_size,
          sort_by: sortConfig?.key, sort_dir: sortConfig?.dir,
        },
      });
      setRecords(r.data);
    } catch { /* ignore */ }
  };
  return (
    <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
      <div>Page {records.page} of {totalPages}</div>
      <div className="flex gap-1">
        <Button size="sm" variant="outline" onClick={() => go(records.page - 1)} disabled={records.page <= 1}>Prev</Button>
        <Button size="sm" variant="outline" onClick={() => go(records.page + 1)} disabled={records.page >= totalPages}>Next</Button>
      </div>
    </div>
  );
}

function Heatmap({ data }) {
  const grid = React.useMemo(() => {
    const m = Array.from({ length: 7 }, () => Array(24).fill(0));
    (data || []).forEach((r) => { if (r.day >= 0 && r.day < 7 && r.hour >= 0 && r.hour < 24) m[r.day][r.hour] = r.count; });
    const max = Math.max(1, ...m.flat());
    return { m, max };
  }, [data]);
  return (
    <div className="overflow-x-auto">
      <table className="text-[10px]">
        <thead><tr><th className="w-8" />{Array.from({ length: 24 }).map((_, h) => <th key={h} className="w-6 text-center font-normal">{h}</th>)}</tr></thead>
        <tbody>{DAYS.map((d, di) => (
          <tr key={d}>
            <td className="text-right pr-1 font-medium">{d}</td>
            {grid.m[di].map((v, hi) => {
              const alpha = v / grid.max;
              return <td key={hi} className="w-6 h-5 border border-white" style={{ background: `rgba(16,185,129,${alpha})` }} title={`${d} ${hi}:00 — ${v}`} />;
            })}
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}
